import { useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { Alert, App, Button, Drawer, Dropdown, FloatButton, Input, Modal, Popconfirm, Space, Spin, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import {
  CommentOutlined, DeleteOutlined, DislikeFilled, DislikeOutlined, HistoryOutlined,
  LikeFilled, LikeOutlined, PlusOutlined, SendOutlined, StopOutlined, WhatsAppOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '@/modules/auth/authStore';
import {
  MaksPanjangAlasanRatingAsisten, MaksPanjangPesanAsisten, PeranAsisten_User, RatingAsisten_Suka, RatingAsisten_TidakSuka,
} from '@/constants/asisten';
import { asistenService } from '../asistenService';
import { useAsistenChat } from '../hooks/useAsistenChat';
import { useKonteksLayar } from '../hooks/useKonteksLayar';
import type { PercakapanAsisten, PesanChat, TiketAsisten } from '../types';
import { IsiJawaban } from './IsiJawaban';
import { LayarPersetujuan } from './LayarPersetujuan';
import '../asisten.css';

const kunciPersetujuan = (username: string) => `asisten-persetujuan-${username}`;

function bacaPersetujuan(username: string): boolean {
  try { return localStorage.getItem(kunciPersetujuan(username)) === '1'; } catch { return false; }
}

function simpanPersetujuan(username: string) {
  try { localStorage.setItem(kunciPersetujuan(username), '1'); } catch { /* abaikan — hanya kenyamanan */ }
}

/**
 * Widget Asisten AI (CS) — tombol "Tanya Booku" + panel samping (asisten-ai-cs.md §7).
 * Dirender MainLayout untuk semua user (22-10-2026); menyembunyikan diri bila tenant di luar
 * daftar uji coba (`Asisten:TenantUjiCoba` di server → GET status 403).
 */
export function AsistenWidget() {
  const { message } = App.useApp(); // bukan message statis — agar ikut tema & konteks antd
  const username = useAuthStore((s) => s.user?.username ?? '');
  const menuAsal = useKonteksLayar();
  const chat = useAsistenChat();
  const [terbuka, setTerbuka] = useState(false);
  const [setuju, setSetuju] = useState(() => bacaPersetujuan(username));
  const [teks, setTeks] = useState('');
  const [riwayat, setRiwayat] = useState<PercakapanAsisten[]>([]);
  const [modalTiket, setModalTiket] = useState(false);
  // 👎 membuka modal alasan (opsional) — bahan tinjauan berkala perbaikan Asisten
  const [pesanDinilaiKurang, setPesanDinilaiKurang] = useState<number | null>(null);
  const [alasanRating, setAlasanRating] = useState('');
  const [ringkasanTiket, setRingkasanTiket] = useState('');
  const [mengirimTiket, setMengirimTiket] = useState(false);
  const ujungDaftar = useRef<HTMLDivElement>(null);

  // Tenant di luar daftar uji coba R1 (atau level kurang) → backend 403 → widget tidak ditampilkan sama sekali
  const [diizinkan, setDiizinkan] = useState(true);
  useEffect(() => {
    asistenService.getStatus().catch((e: unknown) => {
      if (isAxiosError(e) && e.response?.status === 403) setDiizinkan(false);
    });
  }, []);

  useEffect(() => { if (terbuka) void chat.muatStatus(); }, [terbuka, chat.muatStatus]);
  useEffect(() => { ujungDaftar.current?.scrollIntoView({ block: 'end' }); }, [chat.pesan, chat.keteranganAlat]);

  const tersedia = chat.status?.tersedia ?? false;

  const kirim = () => {
    if (!teks.trim()) return;
    void chat.kirim(teks, menuAsal);
    setTeks('');
  };

  const muatRiwayat = async (buka: boolean) => {
    if (!buka) return;
    try { setRiwayat(await asistenService.getDaftarPercakapan()); }
    catch { message.error('Riwayat tidak dapat dimuat.'); }
  };

  const menuRiwayat: MenuProps['items'] = riwayat.length === 0
    ? [{ key: 'kosong', label: 'Belum ada riwayat', disabled: true }]
    : riwayat.map((r) => ({
      key: r.idPercakapan,
      label: r.judul || '(tanpa judul)',
      onClick: () => void chat.muatPercakapan(r.idPercakapan).catch(() => message.error('Percakapan tidak dapat dimuat.')),
    }));

  const hapusRiwayat = async () => {
    try {
      const jumlah = await asistenService.hapusRiwayat();
      chat.percakapanBaru();
      message.success(`${jumlah} percakapan dihapus.`);
    } catch { message.error('Riwayat tidak dapat dihapus.'); }
  };

  const kirimTiket = async () => {
    if (!ringkasanTiket.trim()) return;
    setMengirimTiket(true);
    try {
      const tiket = await asistenService.buatTiket(ringkasanTiket, menuAsal, chat.idPercakapan);
      chat.tambahTiket(`Tiket **${tiket.nomorTiket}** sudah dibuat.`, tiket);
      setModalTiket(false);
      setRingkasanTiket('');
    } catch { message.error('Tiket tidak dapat dibuat.'); }
    finally { setMengirimTiket(false); }
  };

  if (!diizinkan) return null;

  return (
    <>
      {!terbuka && (
        <FloatButton
          className="asisten-tombol"
          type="primary"
          icon={<CommentOutlined />}
          tooltip="Tanya Booku"
          onClick={() => setTerbuka(true)}
        />
      )}

      <Drawer
        title="Asisten Booku"
        placement="right"
        open={terbuka}
        onClose={() => setTerbuka(false)}
        mask={false}
        styles={{ body: { padding: 0 } }}
        extra={setuju && (
          <Space size={4}>
            <Tooltip title="Percakapan baru">
              <Button size="small" icon={<PlusOutlined />} onClick={chat.percakapanBaru} />
            </Tooltip>
            <Dropdown menu={{ items: menuRiwayat }} trigger={['click']} onOpenChange={(buka) => void muatRiwayat(buka)}>
              <Tooltip title="Riwayat"><Button size="small" icon={<HistoryOutlined />} /></Tooltip>
            </Dropdown>
            <Popconfirm title="Hapus seluruh riwayat percakapan Anda?" okText="Hapus" cancelText="Batal" onConfirm={() => void hapusRiwayat()}>
              <Tooltip title="Hapus riwayat saya"><Button size="small" icon={<DeleteOutlined />} /></Tooltip>
            </Popconfirm>
          </Space>
        )}
      >
        {!setuju ? (
          <LayarPersetujuan onSetuju={() => { simpanPersetujuan(username); setSetuju(true); }} />
        ) : (
          <div className="asisten-panel">
            <div className="asisten-daftar">
              {chat.status && !tersedia && (
                <Alert
                  type="warning"
                  showIcon
                  title={chat.status.alasan ?? 'Asisten belum tersedia.'}
                  action={chat.status.tautanWhatsAppCs && (
                    <Button size="small" icon={<WhatsAppOutlined />} href={chat.status.tautanWhatsAppCs} target="_blank" rel="noopener noreferrer">
                      Hubungi CS
                    </Button>
                  )}
                />
              )}
              {chat.pesan.length === 0 && tersedia && (
                <div className="asisten-kosong">
                  Tanyakan cara memakai Booku, konsep akuntansi,<br />atau kendala yang Anda alami.
                </div>
              )}
              {chat.pesan.map((p, i) => (
                <GelembungPesan
                  key={p.kunci}
                  pesan={p}
                  sedangMengalir={chat.sedangMenjawab && i === chat.pesan.length - 1}
                  onRating={(rating) => {
                    if (!p.idPesan) return;
                    if (rating === RatingAsisten_TidakSuka) { setAlasanRating(''); setPesanDinilaiKurang(p.idPesan); return; }
                    void chat.beriRating(p.idPesan, rating).catch(() => message.error('Rating tidak tersimpan.'));
                  }}
                />
              ))}
              {chat.keteranganAlat && <div className="asisten-alat"><Spin size="small" /> {chat.keteranganAlat}</div>}
              <div ref={ujungDaftar} />
            </div>

            <div className="asisten-kaki">
              <Input.TextArea
                value={teks}
                onChange={(e) => setTeks(e.target.value)}
                onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); kirim(); } }}
                placeholder="Tulis pertanyaan… (Shift+Enter untuk baris baru)"
                autoSize={{ minRows: 2, maxRows: 6 }}
                maxLength={MaksPanjangPesanAsisten}
                disabled={!tersedia}
              />
              <div className="asisten-kaki-info">
                <span>{chat.status && tersedia ? `Sisa kuota hari ini: ${chat.status.sisaKuotaHariIni}` : ''}</span>
                <Space size={4}>
                  <Button size="small" type="link" icon={<WhatsAppOutlined />} onClick={() => setModalTiket(true)} disabled={!tersedia}>
                    Teruskan ke tim CS
                  </Button>
                  {chat.sedangMenjawab ? (
                    <Button size="small" icon={<StopOutlined />} onClick={chat.berhenti}>Berhenti</Button>
                  ) : (
                    <Button size="small" type="primary" icon={<SendOutlined />} onClick={kirim} disabled={!tersedia || !teks.trim()}>Kirim</Button>
                  )}
                </Space>
              </div>
            </div>
          </div>
        )}
      </Drawer>

      <Modal
        title="Teruskan ke tim CS"
        open={modalTiket}
        onCancel={() => setModalTiket(false)}
        onOk={() => void kirimTiket()}
        okText="Buat tiket"
        cancelText="Batal"
        confirmLoading={mengirimTiket}
        okButtonProps={{ disabled: !ringkasanTiket.trim() }}
      >
        <p style={{ fontSize: 'var(--font-size-sm)' }}>
          Jelaskan singkat kendala Anda. Jangan menuliskan data pribadi. Setelah tiket dibuat, kirimkan ke WhatsApp CS lewat tombol yang muncul.
        </p>
        <Input.TextArea value={ringkasanTiket} onChange={(e) => setRingkasanTiket(e.target.value)} autoSize={{ minRows: 3, maxRows: 8 }} maxLength={MaksPanjangPesanAsisten} />
      </Modal>

      <Modal
        title="Jawaban kurang membantu"
        open={pesanDinilaiKurang !== null}
        onCancel={() => setPesanDinilaiKurang(null)}
        onOk={() => {
          if (pesanDinilaiKurang === null) return;
          void chat.beriRating(pesanDinilaiKurang, RatingAsisten_TidakSuka, alasanRating.trim() || undefined)
            .then(() => message.success('Terima kasih, masukan Anda kami catat.'))
            .catch(() => message.error('Rating tidak tersimpan.'));
          setPesanDinilaiKurang(null);
        }}
        okText="Kirim"
        cancelText="Batal"
      >
        <p style={{ fontSize: 'var(--font-size-sm)' }}>
          Apa yang kurang tepat atau kurang lengkap? (opsional) Masukan Anda dipakai untuk memperbaiki Asisten. Jangan menuliskan data pribadi.
        </p>
        <Input.TextArea value={alasanRating} onChange={(e) => setAlasanRating(e.target.value)} autoSize={{ minRows: 3, maxRows: 6 }}
          maxLength={MaksPanjangAlasanRatingAsisten} showCount placeholder="Mis. langkahnya berbeda dengan yang saya lihat di layar" />
      </Modal>
    </>
  );
}

function GelembungPesan({ pesan, sedangMengalir, onRating }: {
  pesan: PesanChat;
  sedangMengalir: boolean;
  onRating: (rating: number) => void;
}) {
  if (pesan.peran === PeranAsisten_User) {
    return <div className="asisten-gelembung asisten-gelembung-user">{pesan.isi}</div>;
  }

  return (
    <div className={`asisten-gelembung asisten-gelembung-asisten${pesan.galat ? ' asisten-gelembung-galat' : ''}`}>
      {pesan.isi ? <IsiJawaban isi={pesan.isi} /> : sedangMengalir && <Spin size="small" />}
      {pesan.tiket && <KartuTiket tiket={pesan.tiket} />}
      {pesan.sumber && pesan.sumber.length > 0 && (
        <div className="asisten-lampiran">
          {pesan.sumber.map((s) => (
            <Button key={s.url} size="small" href={s.url} target="_blank" rel="noopener noreferrer">📖 {s.judul}</Button>
          ))}
        </div>
      )}
      {pesan.idPesan && !pesan.galat && !sedangMengalir && (
        <div className="asisten-rating">
          <Tooltip title="Jawaban membantu">
            <Button size="small" type="text" onClick={() => onRating(RatingAsisten_Suka)}
              icon={pesan.rating === RatingAsisten_Suka ? <LikeFilled /> : <LikeOutlined />} />
          </Tooltip>
          <Tooltip title="Jawaban kurang membantu">
            <Button size="small" type="text" onClick={() => onRating(RatingAsisten_TidakSuka)}
              icon={pesan.rating === RatingAsisten_TidakSuka ? <DislikeFilled /> : <DislikeOutlined />} />
          </Tooltip>
        </div>
      )}
    </div>
  );
}

function KartuTiket({ tiket }: { tiket: TiketAsisten }) {
  return (
    <Alert
      style={{ marginTop: 'var(--spacing-xs)' }}
      type="success"
      title={`Tiket ${tiket.nomorTiket}`}
      description={tiket.tautanWhatsApp ? (
        <Button type="primary" size="small" icon={<WhatsAppOutlined />} href={tiket.tautanWhatsApp} target="_blank" rel="noopener noreferrer">
          Kirim ke WhatsApp CS
        </Button>
      ) : `Sampaikan nomor tiket ${tiket.nomorTiket} kepada tim CS Booku.`}
    />
  );
}
